
import React from 'react';
import { Helmet } from 'react-helmet';

// Helper function to create schema.org JSON-LD data
export const createLocalBusinessSchema = (business: {
  name: string;
  description: string;
  url: string;
  logo: string;
  telephone: string;
  address: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  geo?: {
    latitude: number;
    longitude: number;
  };
  openingHours?: string[];
  sameAs?: string[];
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": business.name,
    "description": business.description,
    "url": business.url,
    "telephone": business.telephone,
    "image": business.logo,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": business.address.streetAddress,
      "addressLocality": business.address.addressLocality,
      "addressRegion": business.address.addressRegion,
      "postalCode": business.address.postalCode,
      "addressCountry": business.address.addressCountry
    }
  };

  // Add optional fields if they exist
  if (business.geo) {
    Object.assign(schema, {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": business.geo.latitude,
        "longitude": business.geo.longitude
      }
    });
  }

  if (business.openingHours) {
    Object.assign(schema, {
      "openingHours": business.openingHours
    });
  }

  if (business.sameAs) {
    Object.assign(schema, {
      "sameAs": business.sameAs
    });
  }

  return JSON.stringify(schema);
};

export const createServiceSchema = (service: {
  name: string;
  description: string;
  provider: {
    name: string;
    url: string;
  };
  serviceArea?: {
    name: string;
    url?: string;
  };
  url: string;
  image?: string;
  offers?: {
    price?: string;
    priceCurrency?: string;
    availability?: string;
  };
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": service.provider.name,
      "url": service.provider.url
    },
    "url": service.url
  };

  // Add optional fields if they exist
  if (service.serviceArea) {
    Object.assign(schema, {
      "areaServed": {
        "@type": "Place",
        "name": service.serviceArea.name,
        ...(service.serviceArea.url && { "url": service.serviceArea.url })
      }
    });
  }

  if (service.image) {
    Object.assign(schema, {
      "image": service.image
    });
  }

  if (service.offers) {
    Object.assign(schema, {
      "offers": {
        "@type": "Offer",
        ...(service.offers.price && { "price": service.offers.price }),
        ...(service.offers.priceCurrency && { "priceCurrency": service.offers.priceCurrency }),
        ...(service.offers.availability && { "availability": service.offers.availability })
      }
    });
  }

  return JSON.stringify(schema);
};

// Helper component to add canonical URL and other SEO tags
export const SEOHead: React.FC<{ 
  title: string;
  description: string;
  canonicalUrl: string;
  ogImage?: string;
  schemaData?: string | string[];
}> = ({ 
  title, 
  description, 
  canonicalUrl, 
  ogImage,
  schemaData
}) => {
  const baseUrl = "https://astrem.co";
  const fullCanonicalUrl = `${baseUrl}${canonicalUrl}`;
  
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph / Social Media */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:type" content="website" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      
      {/* Schema.org JSON-LD (structured data) */}
      {schemaData && typeof schemaData === 'string' && (
        <script type="application/ld+json">{schemaData}</script>
      )}
      
      {/* If multiple schema objects are passed */}
      {schemaData && Array.isArray(schemaData) && schemaData.map((schema, index) => (
        <script key={index} type="application/ld+json">{schema}</script>
      ))}
    </Helmet>
  );
};

// Business data for schema
export const astremBusinessData = {
  name: "Astrem",
  description: "A detail-oriented creative partner for businesses, organizations and individuals, specializing in websites, content and marketing solutions based in Baton Rouge, Louisiana.",
  url: "https://astrem.co",
  logo: "https://astrem.co/logo.png",
  telephone: "225-331-6114",
  address: {
    streetAddress: "123 Main St",
    addressLocality: "Baton Rouge",
    addressRegion: "LA",
    postalCode: "70801",
    addressCountry: "US"
  },
  sameAs: [
    "https://facebook.com/astremco",
    "https://instagram.com/astremco",
    "https://linkedin.com/company/astremco"
  ]
};
